/**
 * GraphStore - Centralized graph database for nodes and relationships
 *
 * Maintains indexes for efficient traversal:
 * - Node index: nodeId → node instance
 * - Relationship index: nodeId → { relationshipPropertyName → [relationshipIds] }
 * - Relationship instances: relationshipId → Relationship instance
 *
 * Relationship IDs format: 'originId-destId' for uniqueness
 * Arrays maintain load order of target nodes
 *
 * Reactive Updates:
 * - Uses Jotai atom families for granular reactivity
 * - Node atoms: one atom per node ID
 * - Relationship list atoms: one atom per (sourceId, relationshipName) pair
 * - Updates to atoms trigger re-renders only in components using those specific atoms
 */

import { atom, createStore } from 'jotai';
import { atomFamily } from 'jotai-family';
import { NeoNodeBase } from './neo-node-base';
import { Relationship } from './relationship';

export type NodeId = string;
export type RelationshipId = string;
export type RelationshipPropertyName = string;

/**
 * Metadata for a relationship index entry
 */
export interface RelationshipIndexMetadata {
  isDagSource?: boolean;  // True if populated by DAG subscription (prevents query overwrites)
  lastUpdated: number;    // Timestamp of last update
}

/**
 * Relationship index entry with data and metadata
 */
export interface RelationshipIndexEntry {
  relationships: Set<RelationshipId>;
  metadata: RelationshipIndexMetadata;
}

/**
 * Relationship index structure
 * Maps nodeId to its relationships organized by property name
 * Each property contains a Set of relationship IDs plus metadata
 */
export interface NodeRelationshipIndex {
  [propertyName: string]: RelationshipIndexEntry;
}

/**
 * Jotai store for GraphStore atoms
 * Separate from React's Jotai provider for server-side and testing use
 */
export const graphStoreJotaiStore = createStore();

/**
 * Atom family for individual nodes
 * One atom per node ID for granular reactivity
 */
export const nodeAtomFamily = atomFamily((nodeId: NodeId) =>
  atom<NeoNodeBase<any, any> | null>(null)
);

/**
 * Atom family for relationship lists
 * One atom per (sourceId:relationshipName) for granular reactivity
 * Stores array of relationship IDs (converted from Set)
 */
export const relationshipListAtomFamily = atomFamily((key: string) =>
  atom<RelationshipId[]>([])
);

/**
 * Callback type for node creation listeners
 */
export type NodeCreationListener = (node: NeoNodeBase<any, any>, nodeType: string) => void;

/**
 * GraphStore singleton for managing the object graph
 */
export class GraphStore {
  private static instance: GraphStore;

  // Node storage
  private nodes: Map<NodeId, NeoNodeBase<any, any>> = new Map();

  // Relationship storage
  private relationships: Map<RelationshipId, Relationship> = new Map();

  // Relationship index: nodeId → { propertyName → Set<relationshipIds> }
  private relationshipIndex: Map<NodeId, NodeRelationshipIndex> = new Map();

  // Track which atoms have been used (for clearing in tests)
  private usedNodeAtoms: Set<NodeId> = new Set();
  private usedRelationshipAtoms: Set<string> = new Set();

  // Node creation listeners for application-level reactivity
  private nodeCreationListeners: Set<NodeCreationListener> = new Set();

  private constructor() {}

  /**
   * Get the singleton instance
   */
  static getInstance(): GraphStore {
    if (!GraphStore.instance) {
      GraphStore.instance = new GraphStore();
    }
    return GraphStore.instance;
  }

  /**
   * Clear all stored data (useful for testing or resetting state)
   * Also clears the Jotai atom store to ensure clean state
   */
  clear(): void {
    // Clear all node atoms
    this.usedNodeAtoms.forEach(nodeId => {
      const atom = nodeAtomFamily(nodeId);
      graphStoreJotaiStore.set(atom, null);
    });

    // Clear all relationship atoms
    this.usedRelationshipAtoms.forEach(atomKey => {
      const atom = relationshipListAtomFamily(atomKey);
      graphStoreJotaiStore.set(atom, []);
    });

    // Clear data structures
    this.nodes.clear();
    this.relationships.clear();
    this.relationshipIndex.clear();

    // Clear tracking sets
    this.usedNodeAtoms.clear();
    this.usedRelationshipAtoms.clear();
  }

  /**
   * Register a node in the store
   * @param node The node to register
   */
  registerNode<T extends NeoNodeBase<any, any>>(node: T): void {
    const nodeId = (node as any).id;
    if (!nodeId) {
      throw new Error('Cannot register node without ID');
    }

    // Check if this is a new node (for creation notification)
    const isNew = !this.nodes.has(nodeId);

    // Store the node
    this.nodes.set(nodeId, node);

    // Initialize relationship index for this node if needed
    if (!this.relationshipIndex.has(nodeId)) {
      this.relationshipIndex.set(nodeId, {});
    }

    // Track this atom for clearing
    this.usedNodeAtoms.add(nodeId);

    // Update atom for reactivity
    graphStoreJotaiStore.set(nodeAtomFamily(nodeId), node);

    // Notify listeners if this is a new node
    if (isNew) {
      const nodeType = (node as any)._label || (node as any).constructor?.name || 'Unknown';
      this.notifyNodeCreated(node, nodeType);
    }
  }

  /**
   * Update a node in the store and trigger atom update
   * This is the preferred method for updating nodes as it ensures reactivity
   * @param node The node to update
   */
  updateNode<T extends NeoNodeBase<any, any>>(node: T): void {
    const nodeId = (node as any).id;
    if (!nodeId) {
      throw new Error('Cannot update node without ID');
    }

    // Check if this is a new node (for creation notification)
    const isNew = !this.nodes.has(nodeId);

    // Update in store
    this.nodes.set(nodeId, node);

    // Track this atom for clearing
    this.usedNodeAtoms.add(nodeId);

    // Update atom to trigger reactivity
    graphStoreJotaiStore.set(nodeAtomFamily(nodeId), node);

    // Notify listeners if this is a new node
    if (isNew) {
      const nodeType = (node as any)._label || (node as any).constructor?.name || 'Unknown';
      this.notifyNodeCreated(node, nodeType);
    }
  }

  /**
   * Delete a node from the store
   * @param nodeId The node ID to delete
   */
  deleteNode(nodeId: NodeId): void {
    this.nodes.delete(nodeId);
    this.relationshipIndex.delete(nodeId);

    // Update atom to trigger reactivity (set to null)
    graphStoreJotaiStore.set(nodeAtomFamily(nodeId), null);
  }

  /** Every node id the store holds (the parity dump reads the store, not a
   *  subscription's maps, so a record left behind is seen). */
  getAllNodeIds(): NodeId[] {
    return Array.from(this.nodes.keys());
  }

  /**
   * Get a node by ID
   */
  getNode<T extends NeoNodeBase<any, any>>(nodeId: NodeId): T | undefined {
    return this.nodes.get(nodeId) as T | undefined;
  }

  /**
   * Subscribe to node creation events
   * @param listener Callback called when a new node is registered
   * @returns Unsubscribe function
   */
  onNodeCreated(listener: NodeCreationListener): () => void {
    this.nodeCreationListeners.add(listener);
    return () => this.nodeCreationListeners.delete(listener);
  }

  /**
   * Notify all listeners of a node creation
   * @private
   */
  private notifyNodeCreated(node: NeoNodeBase<any, any>, nodeType: string): void {
    this.nodeCreationListeners.forEach(listener => {
      try {
        listener(node, nodeType);
      } catch (error) {
        console.error('Error in node creation listener:', error);
      }
    });
  }

  /**
   * A relationship's ID: origin, the origin's property and destination, with
   * '::' as delimiter to avoid conflicts with dashes in node IDs. The
   * PROPERTY is part of the identity: two relationships of different types
   * between one pair (a part that both Requires and Satisfies another) are
   * two relationships — keyed by the pair alone they shared one entry and its
   * properties, and removing one emptied the other's (VC-UI fix 7; the wire
   * contract keys an edge by (source, rel, target), and a property names one
   * (rel, direction) per class). Query results build IDs the same way.
   */
  static relationshipId(originId: NodeId, propertyName: RelationshipPropertyName, destId: NodeId): RelationshipId {
    return `${originId}::${propertyName}::${destId}`;
  }

  private createRelationshipId(originId: NodeId, destId: NodeId, propertyName: RelationshipPropertyName): RelationshipId {
    return GraphStore.relationshipId(originId, propertyName, destId);
  }

  /**
   * Register a relationship in the store
   * @param relationship The relationship instance
   * @param propertyName The property name on the origin node (e.g., 'steps')
   */
  /**
   * Internal method to register a relationship with metadata
   * @param relationship The relationship instance
   * @param propertyName The property name
   * @param isDagSource Whether this is from a DAG subscription
   * @private
   */
  private registerRelationshipInternal(
    relationship: Relationship,
    propertyName: RelationshipPropertyName,
    isDagSource: boolean = false
  ): void {
    const originId = relationship.sourceId;
    const destId = relationship.targetId;
    const relId = this.createRelationshipId(originId, destId, propertyName);

    // Store the relationship instance
    this.relationships.set(relId, relationship);

    // Update origin node's relationship index
    const originIndex = this.relationshipIndex.get(originId) || {};
    if (!originIndex[propertyName]) {
      originIndex[propertyName] = {
        relationships: new Set(),
        metadata: {
          isDagSource,
          lastUpdated: Date.now()
        }
      };
    }

    // Add to Set (automatically maintains uniqueness)
    originIndex[propertyName].relationships.add(relId);

    // Update metadata
    originIndex[propertyName].metadata.lastUpdated = Date.now();
    if (isDagSource) {
      originIndex[propertyName].metadata.isDagSource = true;
    }

    this.relationshipIndex.set(originId, originIndex);

    // Track this atom for clearing
    const atomKey = this.makeRelationshipListKey(originId, propertyName);
    this.usedRelationshipAtoms.add(atomKey);

    // Update atom for reactivity (convert Set to Array)
    const newRelIds = Array.from(originIndex[propertyName].relationships);

    graphStoreJotaiStore.set(
      relationshipListAtomFamily(atomKey),
      newRelIds
    );
  }

  registerRelationship(relationship: Relationship, propertyName: RelationshipPropertyName): void {
    this.registerRelationshipInternal(relationship, propertyName, false);
  }

  /**
   * Add a relationship to the store (convenience method with atom update)
   * This is the preferred method for adding relationships as it ensures reactivity
   * @param sourceId Source node ID
   * @param targetId Target node ID
   * @param propertyName Property name on the source node
   * @param relationship Optional relationship instance (created if not provided)
   * @param options Options for relationship creation
   */
  addRelationship(
    sourceId: NodeId,
    targetId: NodeId,
    propertyName: RelationshipPropertyName,
    relationship?: Relationship,
    options?: { isDagSource?: boolean; properties?: Record<string, any> }
  ): void {
    const isDagSource = options?.isDagSource || false;

    // Debug hook: log every add for the watched node id.
    if (typeof window !== "undefined") {
      const watchedId =
        (window as any).__debugNode ||
        (typeof localStorage !== "undefined" &&
          localStorage.getItem("__debugNode")) ||
        undefined;
      if (watchedId && (sourceId === watchedId || targetId === watchedId)) {
        // eslint-disable-next-line no-console
        console.log(
          `%c[GS-DEBUG] addRelationship`,
          "color: teal;",
          {
            source: sourceId.slice(0, 8),
            target: targetId.slice(0, 8),
            propertyName,
            isDagSource,
          }
        );
      }
    }

    // Check if we're trying to overwrite DAG-sourced data with query data
    const existingEntry = this.relationshipIndex.get(sourceId)?.[propertyName];
    if (existingEntry?.metadata.isDagSource && !isDagSource) {
      console.warn('Cannot overwrite DAG-sourced relationship with query data', {
        sourceId,
        propertyName,
        attemptedIsDagSource: isDagSource,
        existingIsDagSource: existingEntry.metadata.isDagSource
      });
      return; // Skip this write
    }

    // Only clear if DAG is overwriting query data (not if appending to existing DAG data)
    // This allows DAG patches to add relationships incrementally without clearing previous DAG data
    if (isDagSource && existingEntry && !existingEntry.metadata.isDagSource) {
      existingEntry.relationships.clear();
    }

    // Create relationship if not provided
    const rel = relationship || new (Relationship as any)(sourceId, targetId, options?.properties);

    // Update properties on existing relationship instance if provided
    if (relationship && options?.properties) {
      relationship.properties = options.properties;
    }

    // Use internal method with metadata
    this.registerRelationshipInternal(rel, propertyName, isDagSource);
  }

  /**
   * Update edge properties on an existing relationship
   */
  updateRelationshipProperties(
    originId: NodeId,
    destId: NodeId,
    properties: Record<string, any>,
    /** the origin's property; omitted, every relationship origin → dest */
    propertyName?: RelationshipPropertyName,
  ): void {
    const originIndex = this.relationshipIndex.get(originId);
    if (!originIndex) return;
    for (const [name, entry] of Object.entries(originIndex)) {
      if (propertyName !== undefined && name !== propertyName) continue;
      const relId = this.createRelationshipId(originId, destId, name);
      const rel = this.relationships.get(relId);
      if (!rel || !entry.relationships.has(relId)) continue;
      rel.properties = properties;
      // Trigger reactivity on the property that references this relationship
      const atomKey = this.makeRelationshipListKey(originId, name);
      graphStoreJotaiStore.set(relationshipListAtomFamily(atomKey), Array.from(entry.relationships));
    }
  }

  /**
   * Remove a relationship from the store
   * @param originId Origin node ID
   * @param destId Destination node ID
   * @param propertyName Property name on the origin node
   */
  /**
   * True when the (sourceId, propertyName) relationship entry is owned by
   * the DAG patch lane. Confirmed-save promotion
   * (EditableRelationshipManager.applyOpsToGraphStore) checks this and
   * leaves DAG-sourced entries to their patches — addRelationship would
   * refuse the downgrade anyway, but with a console warning per call.
   */
  isDagSourced(sourceId: NodeId, propertyName: RelationshipPropertyName): boolean {
    return !!this.relationshipIndex.get(sourceId)?.[propertyName]?.metadata.isDagSource;
  }

  removeRelationship(originId: NodeId, destId: NodeId, propertyName: RelationshipPropertyName): void {
    const relId = this.createRelationshipId(originId, destId, propertyName);

    // Remove from relationships map
    this.relationships.delete(relId);

    // Remove from origin node's index
    const originIndex = this.relationshipIndex.get(originId);
    const entry = originIndex?.[propertyName];
    if (entry) {
      entry.relationships.delete(relId);

      // Update metadata
      entry.metadata.lastUpdated = Date.now();

      // Clean up empty entries
      if (entry.relationships.size === 0) {
        delete originIndex![propertyName];
      }

      // Track this atom for clearing
      const atomKey = this.makeRelationshipListKey(originId, propertyName);
      this.usedRelationshipAtoms.add(atomKey);

      // Update atom for reactivity (convert Set to Array)
      const updatedEntry = originIndex?.[propertyName];
      graphStoreJotaiStore.set(
        relationshipListAtomFamily(atomKey),
        updatedEntry ? Array.from(updatedEntry.relationships) : []
      );
    }
  }

  /**
   * Get a relationship instance by ID
   * @param relId Relationship ID
   */
  getRelationship(relId: RelationshipId): Relationship | undefined {
    return this.relationships.get(relId);
  }

  /**
   * Get relationship IDs for a node property
   * @param nodeId The node ID
   * @param propertyName The relationship property name
   * @returns Array of relationship IDs
   */
  getRelationshipIds(nodeId: NodeId, propertyName: RelationshipPropertyName): RelationshipId[] {
    const nodeIndex = this.relationshipIndex.get(nodeId);
    const entry = nodeIndex?.[propertyName];
    return entry ? Array.from(entry.relationships) : [];
  }

  /**
   * Get relationship metadata for a node property
   * @param nodeId The node ID
   * @param propertyName The relationship property name
   * @returns Metadata or undefined
   */
  getRelationshipMetadata(nodeId: NodeId, propertyName: RelationshipPropertyName): RelationshipIndexMetadata | undefined {
    const nodeIndex = this.relationshipIndex.get(nodeId);
    return nodeIndex?.[propertyName]?.metadata;
  }

  /**
   * Create a key for relationship list atom
   * Format: "sourceId:propertyName"
   */
  private makeRelationshipListKey(sourceId: NodeId, propertyName: RelationshipPropertyName): string {
    if (sourceId === undefined || sourceId === null) {
      console.error('makeRelationshipListKey called with undefined/null sourceId:', { sourceId, propertyName });
      return `undefined:${propertyName}`;
    }
    if (propertyName === undefined || propertyName === null) {
      console.error('makeRelationshipListKey called with undefined/null propertyName:', { sourceId, propertyName });
      return `${sourceId}:undefined`;
    }
    return `${sourceId}:${propertyName}`;
  }

  /**
   * Get atom for a specific node (for use in hooks)
   * @param nodeId The node ID
   */
  getNodeAtom(nodeId: NodeId) {
    return nodeAtomFamily(nodeId);
  }

  /**
   * Get atom for a relationship list (for use in hooks)
   * @param sourceId The source node ID
   * @param propertyName The relationship property name
   */
  getRelationshipListAtom(sourceId: NodeId, propertyName: RelationshipPropertyName) {
    const key = this.makeRelationshipListKey(sourceId, propertyName);
    return relationshipListAtomFamily(key);
  }

  /**
   * Get all relationships for a node property
   * @param nodeId The node ID
   * @param propertyName The relationship property name
   * @returns Array of relationship instances in load order
   */
  getRelationships(nodeId: NodeId, propertyName: RelationshipPropertyName): Relationship[] {
    const nodeIndex = this.relationshipIndex.get(nodeId);
    const entry = nodeIndex?.[propertyName];
    if (!entry) {
      return [];
    }

    return Array.from(entry.relationships)
      .map((relId: string) => this.relationships.get(relId))
      .filter((rel): rel is Relationship => rel !== undefined);
  }

  /**
   * Get target nodes for a relationship property
   * @param nodeId The node ID
   * @param propertyName The relationship property name
   * @returns Array of target nodes in load order
   */
  getTargetNodes<T extends NeoNodeBase<any, any>>(
    nodeId: NodeId,
    propertyName: RelationshipPropertyName
  ): T[] {
    const relationships = this.getRelationships(nodeId, propertyName);
    return relationships
      .map(rel => this.nodes.get(rel.targetId) as T)
      .filter((node): node is T => node !== undefined);
  }

  /**
   * Get all relationship property names for a node
   * @param nodeId The node ID
   * @returns Array of property names that have relationships
   */
  getRelationshipProperties(nodeId: NodeId): RelationshipPropertyName[] {
    const nodeIndex = this.relationshipIndex.get(nodeId);
    return nodeIndex ? Object.keys(nodeIndex) : [];
  }

  /**
   * Check if a relationship exists
   * @param originId Origin node ID
   * @param destId Destination node ID
   * @param propertyName Property name on the origin node
   */
  hasRelationship(originId: NodeId, destId: NodeId, propertyName: RelationshipPropertyName): boolean {
    const relId = this.createRelationshipId(originId, destId, propertyName);
    const nodeIndex = this.relationshipIndex.get(originId);
    const entry = nodeIndex?.[propertyName];
    return !!(entry && entry.relationships.has(relId));
  }

  /**
   * Get relationship count for a property
   * @param nodeId The node ID
   * @param propertyName The relationship property name
   */
  getRelationshipCount(nodeId: NodeId, propertyName: RelationshipPropertyName): number {
    const nodeIndex = this.relationshipIndex.get(nodeId);
    return nodeIndex?.[propertyName]?.relationships.size ?? 0;
  }

  /**
   * Debug: Get the entire relationship index
   * Useful for debugging and testing
   */
  getDebugInfo(): {
    nodeCount: number;
    relationshipCount: number;
    relationshipIndex: Map<NodeId, NodeRelationshipIndex>;
  } {
    return {
      nodeCount: this.nodes.size,
      relationshipCount: this.relationships.size,
      relationshipIndex: new Map(this.relationshipIndex),
    };
  }
}

// Export singleton instance
export const graphStore = GraphStore.getInstance();

// Debug: expose on window in dev so we can inspect state from devtools.
// Guard with NODE_ENV so prod bundles don't leak internals.
if (
  typeof window !== "undefined" &&
  process.env.NODE_ENV !== "production"
) {
  (window as any).__graphStore = graphStore;
  (window as any).__jotaiStore = graphStoreJotaiStore;
}